import { Box, Fab, useMediaQuery, useTheme } from "@mui/material";
import { Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../../../common/components/Toast";
import {
  getReportTemplateById,
  updateReportTemplate,
} from "../../api/report-templates";
import ReportPreview from "../../components/ReportPreview";
import TemplateCustomizer from "../../components/TemplateCustomizer";

export default function EditReportTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [saveStatus, setSaveStatus] = useState("saved"); // 'idle', 'saving', 'saved'
  const [lastSaved, setLastSaved] = useState(null);
  const [, setUpdateTrigger] = useState(0); // Used to trigger re-renders for time display
  const saveTimeoutRef = useRef(null);
  const [customizerOpen, setCustomizerOpen] = useState(false);

  // Update the time display every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger((prev) => prev + 1);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    getReportTemplateById(id)
      .then((data) => {
        setFormData({
          title: data.title || "",
          logo: data.logo || null,
          company_name: data.company_name || "",
          address: data.address || "",
          location: data.location || "",
          notes: data.notes || "",
          // Section toggles
          include_project_summary: data.include_project_summary ?? true,
          include_asset_breakdown: data.include_asset_breakdown ?? true,
          // Project summary columns
          include_total_assets: data.include_total_assets ?? true,
          include_total_impressions: data.include_total_impressions ?? true,
          include_total_royalty_revenue:
            data.include_total_royalty_revenue ?? true,
          include_total_impression_revenue:
            data.include_total_impression_revenue ?? true,
          include_total_revenue: data.include_total_revenue ?? true,
          // Asset breakdown columns
          include_sales_revenue: data.include_sales_revenue ?? true,
          include_impressions: data.include_impressions ?? true,
          include_impressions_revenue: data.include_impressions_revenue ?? true,
        });
      })
      .catch(() => {
        toast.error("Failed to load template.");
        navigate("/report-templates");
      });
  }, [id, navigate]);

  const autoSave = useCallback(
    async (data) => {
      try {
        setSaveStatus("saving");
        await updateReportTemplate(id, data);
        setErrors({});
        setSaveStatus("saved");
        setLastSaved(new Date());
      } catch (error) {
        setErrors(error);
        setSaveStatus("idle");
        console.error("Auto-save failed:", error);
      }
    },
    [id],
  );

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    let newValue;

    if (type === "checkbox") {
      newValue = checked;
    } else if (type === "file") {
      newValue = files[0];
    } else {
      newValue = value;
    }

    setFormData((prev) => {
      const updatedData = { ...prev, [name]: newValue };

      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set a new timeout to auto-save after 1 second of inactivity
      saveTimeoutRef.current = setTimeout(() => {
        autoSave(updatedData);
      }, 1000);

      return updatedData;
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (!formData)
    return <div data-testid="edit-template-loading">Loading...</div>;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0,
        pr: { xs: 0, md: "350px" },
      }}
      data-testid="edit-template-page"
    >
      <ReportPreview formData={formData} />

      <TemplateCustomizer
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        saveStatus={saveStatus}
        lastSaved={lastSaved}
        mobileOpen={customizerOpen}
        onMobileClose={() => setCustomizerOpen(false)}
      />

      {isMobile && (
        <Fab
          color="primary"
          size="medium"
          onClick={() => setCustomizerOpen(true)}
          sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 10 }}
        >
          <Settings size={20} />
        </Fab>
      )}
    </Box>
  );
}
